import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Divider,
  IconButton,
  AppBar,
  Toolbar,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft, ChevronRight, Check, Circle, ArrowBack, Menu as MenuIcon, WarningAmberRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { brand, colors, navItemSx, numStyle, topBarSx } from "../theme";
import { createModelSubheaders } from "../utils/modelTypeConstants";

import { PixelDots } from './ConstructionLoader';
const SIDEBAR_WIDTH = 280;

interface ModelStepperProps {
  steps: string[];
  activeStep: number;
  completedSteps: number[];
  onStepChange: (currentStep: number, nextStep: number) => void;
  onNext: () => void;
  onBack: () => void;
  isStepComplete: (step: number) => boolean;
  /** Titles of the fields on this step that are not yet satisfied. */
  stepBlockers?: string[];
  isCreating?: boolean;
  leveredIrr?: string;
  leveredMoic?: string;
  finalMetricsCalculating?: boolean;
  finalMetricsCalculating2?: boolean;
  isDebouncing?: boolean;
  children?: React.ReactNode;
  showRetail?: boolean;
  existingModel?: boolean;
  handleSaveAndExit?: () => void;
  modelDetails?: any;
  unitSqFtTotal?: number;
  retailSqFtTotal?: number;
}

const ModelStepper: React.FC<ModelStepperProps> = ({
  steps,
  activeStep,
  completedSteps,
  onStepChange,
  onNext,
  onBack,
  isStepComplete,
  stepBlockers = [],
  isCreating = false,
  leveredIrr = "",
  leveredMoic = "",
  finalMetricsCalculating = false,
  finalMetricsCalculating2 = false,
  isDebouncing = false,
  children,
  showRetail,
  existingModel = false,
  handleSaveAndExit,
  modelDetails,
  unitSqFtTotal,
  retailSqFtTotal,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const completedCount = completedSteps.length;
  const totalSteps = steps.length;
  const progressPercentage = (completedCount / totalSteps) * 100;

  const isBranchCompleted = (stepIndex: number) => completedSteps.includes(stepIndex + 1);
  const isCurrentStep = (stepIndex: number) => stepIndex === activeStep;

  const sheetUrl = modelDetails?.google_sheet_url ?? '';
  // Only block nav for critical operations (sheet creation, intermediate processing).
  // Field-level recalculations (finalMetricsCalculating) run in background without blocking.
  const isNavDisabled =
    isCreating ||
    isDebouncing ||
    finalMetricsCalculating2 ||
    !sheetUrl ||
    sheetUrl === "";


  const currentStepTitle = steps[activeStep];
  const stepDescription = createModelSubheaders[currentStepTitle as keyof typeof createModelSubheaders];

  // Residential + retail square feet may not exceed the property's Gross
  // Square Feet. Guarded on a positive GSF so models that never set one (or
  // hold a blank / non-numeric value) are never blocked.
  const grossSqFtField = modelDetails?.user_model_field_values?.find(
    (f: any) => f && f.field_key === 'Gross Square Feet'
  );
  const grossSqFtValue = grossSqFtField ? Number(grossSqFtField.value) : NaN;
  const combinedSqFt = (unitSqFtTotal || 0) + (retailSqFtTotal || 0);
  const isSqFtBlocker =
    !isNaN(grossSqFtValue) &&
    grossSqFtValue > 0 &&
    combinedSqFt > grossSqFtValue &&
    (currentStepTitle === 'Residential Rental Units' || currentStepTitle === 'Retail Income');

  const isNextDisabled = !isStepComplete(activeStep) || isSqFtBlocker;

  // Never disable Continue silently. A greyed-out button with no explanation is
  // indistinguishable from a page that is still loading — that ambiguity cost a
  // full day of production time on 2026-08-20, when an unsatisfiable check left
  // every existing model stuck on General Property Assumptions and the only
  // signal was a button that never lit up.
  const waitingOnWorkbook = !sheetUrl;
  const disabledReason = (() => {
    if (isCreating) return 'Saving your changes…';
    if (finalMetricsCalculating2) return 'Recalculating the model…';
    if (waitingOnWorkbook) return 'Preparing the model workbook…';
    if (isSqFtBlocker) return null; // already explained by its own chip
    if (!isStepComplete(activeStep)) {
      if (stepBlockers.length === 0) {
        return 'This step is incomplete. If nothing on this page looks blank, refresh — and tell us, because that is a bug.';
      }
      const shown = stepBlockers.slice(0, 3).join(', ');
      const more = stepBlockers.length > 3 ? ` +${stepBlockers.length - 3} more` : '';
      return `Needs a value: ${shown}${more}`;
    }
    return null;
  })();

  // Display-only label remaps. The raw `step` string remains the navigation
  // key everywhere else; only the visible text changes.
  const loanNameField = modelDetails?.user_model_field_values?.find(
    (f: any) => f && typeof f.field_key === 'string' && f.field_key.trim() === 'Loan Name'
  );
  const secondLienLabel =
    (loanNameField?.value && String(loanNameField.value).trim()) || 'Pref / Mezz Loan';
  const getStepLabel = (step: string) => {
    if (step === 'Legal and Pre-Development Costs') return 'Legal and Setup Costs';
    if (step === 'Second Lien') return secondLienLabel;
    return step;
  };

  const sidebarContent = (
    <>
        {/* Header */}
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton
              onClick={() => navigate('/')}
              size="small"
              sx={{
                color: colors.white,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <Typography
              variant="caption"
              sx={{
                color: brand.accentNav,
                fontSize: '0.8125rem',
                cursor: 'pointer',
                '&:hover': {
                  color: colors.white,
                },
              }}
              onClick={() => navigate('/')}
            >
              Back to Your Models
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: colors.white,
              fontWeight: 600,
              mb: 1,
            }}
          >
            {existingModel ? 'Edit Model' : 'Create Model'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: brand.mutedDarkBg,
              fontSize: '0.75rem',
            }}
          >
            Step {activeStep + 1} of {totalSteps}
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ px: 3, pb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: brand.lineDarkBg,
              '& .MuiLinearProgress-bar': {
                bgcolor: brand.accent,
                borderRadius: 3,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: brand.mutedDarkBg,
              fontSize: '0.7rem',
              mt: 0.5,
              display: 'block',
            }}
          >
            {completedCount} of {totalSteps} completed
          </Typography>
        </Box>

        <Divider sx={{ borderColor: brand.lineDarkBg }} />

        {/* Steps List */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 2,
            opacity: isNavDisabled ? 0.5 : 1,
            pointerEvents: isNavDisabled ? 'none' : 'auto',
          }}
        >
          <List sx={{ p: 0 }}>
            {steps.map((step, index) => {
              const completed = isBranchCompleted(index + 1);
              const current = isCurrentStep(index);
              const canNavigate = completed || isBranchCompleted(index - 1) || index === 0;
              // An incomplete step blocks moving FORWARD, never backward. Going
              // back to an earlier step is how a user gets out of a step they
              // cannot satisfy — on 2026-08-20 this was disabled too, so a
              // stuck user had no way out of the wizard at all.
              const stepNavBlocked = isNextDisabled && index > activeStep;

              return (
                <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      if (canNavigate && !stepNavBlocked) {
                        onStepChange(activeStep, index);
                        setMobileOpen(false);
                      }
                    }}
                    disabled={!canNavigate || stepNavBlocked}
                    sx={{
                      ...navItemSx(current),
                      px: 1.5,
                      '&.Mui-disabled': {
                        opacity: 0.4,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {completed || existingModel ? (
                        <Check sx={{ color: brand.accent, fontSize: '1.25rem' }} />
                      ) : (
                        <Circle
                          sx={{
                            color: current ? colors.white : 'rgba(255,255,255,0.4)',
                            fontSize: current ? '0.75rem' : '0.5rem',
                          }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={getStepLabel(step)}
                      primaryTypographyProps={{
                        fontWeight: current ? 600 : 500,
                        fontSize: '0.875rem',
                        sx: {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Metrics Section */}
        {(leveredIrr || leveredMoic) && (
          <>
            <Divider sx={{ borderColor: brand.lineDarkBg }} />
            <Box sx={{ p: 2, bgcolor: brand.ink2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: brand.mutedDarkBg,
                  fontSize: '0.7rem',
                  display: 'block',
                  mb: 1,
                }}
              >
                Current Metrics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {leveredIrr && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: brand.mutedDarkBg, fontSize: '0.7rem' }}
                    >
                      Levered IRR
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: finalMetricsCalculating ? 'rgba(255,255,255,0.4)' : colors.white,
                        fontWeight: 400,
                        fontSize: '1rem',
                        ...numStyle,
                      }}
                    >
                      {leveredIrr}
                      {finalMetricsCalculating && <Box component="span" sx={{ ml: 1 }}><PixelDots size={8} surface="dark" label="Calculating" /></Box>}
                    </Typography>
                  </Box>
                )}
                {leveredMoic && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: brand.mutedDarkBg, fontSize: '0.7rem' }}
                    >
                      Levered MOIC
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: finalMetricsCalculating ? 'rgba(255,255,255,0.4)' : colors.white,
                        fontWeight: 400,
                        fontSize: '1rem',
                        ...numStyle,
                      }}
                    >
                      {leveredMoic}
                      {finalMetricsCalculating && <Box component="span" sx={{ ml: 1 }}><PixelDots size={8} surface="dark" label="Calculating" /></Box>}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile top bar — step context + progress + drawer toggle */}
      {isMobile && (
        <AppBar position="fixed" elevation={0} sx={{ ...topBarSx, position: 'fixed', backgroundImage: 'none', zIndex: (t) => t.zIndex.drawer + 1 }}>
          <Toolbar sx={{ minHeight: 56, gap: 1, px: 1.5 }}>
            <IconButton edge="start" aria-label="open steps" onClick={() => setMobileOpen(true)} sx={{ color: colors.white }}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ color: colors.white, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
                {getStepLabel(currentStepTitle)}
              </Typography>
              <Typography sx={{ color: brand.mutedDarkBg, fontSize: '0.7rem' }}>
                Step {activeStep + 1} of {totalSteps}
              </Typography>
            </Box>
          </Toolbar>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{ height: 3, bgcolor: 'rgba(255,255,255,0.15)', '& .MuiLinearProgress-bar': { bgcolor: brand.accent } }}
          />
        </AppBar>
      )}

      {/* Step navigation: permanent rail on desktop, temporary drawer on mobile */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, boxSizing: 'border-box', bgcolor: colors.navy, borderRadius: 0, display: 'flex', flexDirection: 'column' } }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            bgcolor: colors.navy,
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            height: '100vh',
            zIndex: 1200,
            borderRight: `1px solid ${brand.lineDarkBg}`,
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          marginLeft: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
          mt: isMobile ? '59px' : 0,
          bgcolor: colors.grey[100],
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
          {/* Content Container - no padding/maxWidth for steps with their own full-width title bar */}
        <Box
          id="create-model-content"
          sx={{
            flex: 1,
            p: 0,
          }}
        >
          {/* Full-width Context Bar */}
          <Box
            sx={{
              ...topBarSx,
              position: 'static',
              py: { xs: 1.5, md: 2 },
              px: { xs: 2, md: 4 },
              width: '100%',
            }}
          >
            <Typography variant="h3" sx={{ color: 'white', fontSize: { xs: '1.15rem', md: '1.75rem' } }}>
              <span data-testid="current-step-title" style={{ fontWeight: 600 }}>
                {currentStepTitle === "Legal and Pre-Development Costs" ? "Legal and Setup Costs" : currentStepTitle}
              </span>
              {modelDetails?.name && (
                <span style={{ fontWeight: 400, textTransform: 'none' }}>
                  : {modelDetails.name}
                </span>
              )}
            </Typography>
          </Box>

          {/* Content Wrapper */}
          <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
            {/* Step Description */}
            {stepDescription && (
              <Typography
                variant="body1"
                sx={{
                  color: colors.grey[700],
                  mb: 3,
                }}
              >
                {stepDescription}
              </Typography>
            )}

            {/* Step Content */}
            <Box
              sx={{
                opacity: finalMetricsCalculating2 ? 0.6 : 1,
                pointerEvents: finalMetricsCalculating2 ? 'none' : 'auto',
                cursor: finalMetricsCalculating2 ? 'wait' : 'default',
              }}
            >
              {children}
            </Box>
          </Box>
        </Box>

        {/* Bottom Navigation */}
        <Box
          sx={{
            marginTop: 'auto',
            borderTop: `1px solid ${colors.grey[300]}`,
            bgcolor: colors.white,
            p: { xs: 2, md: 3 },
            gap: 1,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={activeStep === 0 || isNavDisabled}
            startIcon={<ChevronLeft />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Previous
          </Button>

          {!isSqFtBlocker && (isNavDisabled || isNextDisabled) && disabledReason && (
            <Typography
              variant="body2"
              data-testid="step-blocked-reason"
              sx={{
                order: { xs: 3, md: 0 },
                width: { xs: '100%', md: 'auto' },
                color: colors.grey[700],
                fontStyle: 'italic',
              }}
            >
              {disabledReason}
            </Typography>
          )}

          {isSqFtBlocker && (
            <Chip
              icon={<WarningAmberRounded />}
              label="Residential + Retail SF exceed Gross Square Feet. Adjust before continuing."
              sx={{
                order: { xs: 3, md: 0 },
                width: { xs: '100%', md: 'auto' },
                height: 'auto',
                py: 0.75,
                bgcolor: colors.errorBg,
                color: colors.error,
                border: `1px solid ${colors.error}`,
                '& .MuiChip-icon': { color: colors.error },
                '& .MuiChip-label': { whiteSpace: 'normal' },
              }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            {existingModel && (
              <Button
                variant="outlined"
                onClick={handleSaveAndExit}
                data-testid="wizard-save-exit"
                disabled={
                  !isStepComplete(activeStep) ||
                  isNavDisabled
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Save & Exit
              </Button>
            )}
            {/* Finish/Continue: hide Finish on last step when editing — same as ModelStepper.old (only Save & Exit completes) */}
            {!(activeStep === steps.length - 1 && existingModel) && (
              <Button
                variant="contained"
                data-testid="wizard-continue"
                onClick={onNext}
                disabled={isNavDisabled || !isStepComplete(activeStep) || isSqFtBlocker}
                endIcon={activeStep === steps.length - 1 ? undefined : <ChevronRight />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: 140,
                }}
              >
                {activeStep === steps.length - 1
                  ? isCreating
                    ? 'Creating Model...'
                    : 'Finish'
                  : existingModel
                  ? 'Next'
                  : 'Continue'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ModelStepper;
